import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiDownload, FiClock, FiCalendar, FiUsers } from 'react-icons/fi';
import { staffAttendanceAPI } from '../services/api';

const StaffAttendanceReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [stats, setStats] = useState({});
  
  const [filters, setFilters] = useState({
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    role_id: '',
    staff_id: ''
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    if (filters.start_date && filters.end_date) {
      fetchReports();
    }
  }, [filters.start_date, filters.end_date, filters.role_id, filters.staff_id]);

  const fetchRoles = async () => {
    try {
      const response = await staffAttendanceAPI.getRoles();
      if (response.data.success) {
        setRoles(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await staffAttendanceAPI.getReport(filters);
      if (response.data.success) {
        setReports(response.data.data);
        setStats(response.data.stats);
      }
    } catch (error) {
      toast.error('Error fetching reports');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const exportToCSV = () => {
    if (reports.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = [
      'Date',
      'Employee ID',
      'Name',
      'Role',
      'Login Time',
      'Logout Time',
      'Total Hours',
      'Status',
      'Holiday Work',
      'Remarks'
    ];

    const csvData = reports.map(record => [
      record.date,
      record.employee_id,
      `${record.name} ${record.surname}`,
      record.role_name,
      record.login_time || '-',
      record.logout_time || '-',
      record.total_hours || '-',
      getStatusText(record.staff_attendance_type_id),
      record.is_holiday_work ? 'Yes' : 'No',
      record.remark || '-'
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `staff_attendance_${filters.start_date}_to_${filters.end_date}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Report exported successfully!');
  };

  const getStatusText = (record) => {
    // For QR-based attendance (has login_time)
    if (record.login_time) {
      if (record.logout_time) {
        return 'Completed';
      } else {
        return 'Present';
      }
    }
    
    // For manual attendance (old system)
    const statuses = {
      1: 'Present',
      2: 'Absent',
      3: 'Half Day',
      4: 'Leave'
    };
    return statuses[record.staff_attendance_type_id] || 'Unknown';
  };

  const getStatusColor = (record) => {
    // For QR-based attendance (has login_time)
    if (record.login_time) {
      if (record.logout_time) {
        return 'bg-blue-100 text-blue-800'; // Completed
      } else {
        return 'bg-green-100 text-green-800'; // Present (still logged in)
      }
    }
    
    // For manual attendance (old system)
    const colors = {
      1: 'bg-green-100 text-green-800',
      2: 'bg-red-100 text-red-800',
      3: 'bg-yellow-100 text-yellow-800',
      4: 'bg-blue-100 text-blue-800'
    };
    return colors[record.staff_attendance_type_id] || 'bg-gray-100 text-gray-800';
  };

  const formatTime = (time) => {
    if (!time) return '-';
    return new Date(`2000-01-01 ${time}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Staff Attendance Reports</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">View staff attendance with login/logout times and working hours</p>
        </div>
        <button
          onClick={exportToCSV}
          disabled={reports.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FiDownload /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              name="start_date"
              value={filters.start_date}
              onChange={handleFilterChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              name="end_date"
              value={filters.end_date}
              onChange={handleFilterChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role/Department
            </label>
            <select
              name="role_id"
              value={filters.role_id}
              onChange={handleFilterChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
            >
              <option value="">All Roles</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={fetchReports}
              disabled={loading}
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Apply Filters'}
            </button>
          </div>
        </div>
      </div>

      {/* Statistics */}
      {stats && Object.keys(stats).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Records</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_records}</p>
              </div>
              <FiUsers className="text-blue-500" size={32} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Present</p>
                <p className="text-2xl font-bold text-green-600">{stats.total_present}</p>
              </div>
              <FiCalendar className="text-green-500" size={32} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Holiday Work</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.total_holiday_work}</p>
              </div>
              <span className="text-2xl">🎉</span>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Hours/Day</p>
                <p className="text-2xl font-bold text-purple-600">{stats.avg_working_hours || 0}h</p>
              </div>
              <FiClock className="text-purple-500" size={32} />
            </div>
          </div>
        </div>
      )}

      {/* Reports Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '1200px' }}>
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Login Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Logout Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Hours
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Remarks
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                    Loading reports...
                  </td>
                </tr>
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                    No attendance records found for the selected period
                  </td>
                </tr>
              ) : (
                reports.map((record, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(record.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.employee_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {record.name} {record.surname}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {record.role_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatTime(record.login_time)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatTime(record.logout_time)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-purple-600">
                        {record.total_hours ? `${record.total_hours}h` : '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(record)}`}>
                        {getStatusText(record)}
                      </span>
                      {record.is_holiday_work === 1 && (
                        <span className="ml-2 text-xs">🎉</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {record.remark || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
        <p className="text-sm text-blue-900 font-semibold mb-2">ℹ️ Report Information:</p>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Login/Logout Times:</strong> Recorded via QR code scanning</li>
          <li>• <strong>Total Hours:</strong> Automatically calculated (Logout - Login)</li>
          <li>• <strong>Holiday Work (🎉):</strong> Staff who worked on holidays/Sundays</li>
          <li>• <strong>Export CSV:</strong> Download report for further analysis</li>
        </ul>
      </div>
    </div>
  );
};

export default StaffAttendanceReports;
